/**
 * Sonner toast helper with consistent duration defaults:
 *   - success → 2.5s (positive, quick acknowledgement)
 *   - info / message → 4s   (neutral notice — give time to read)
 *   - error / warning → 6s  (need a moment to register & react)
 *
 * Drop-in for `import { toast } from "sonner"` — re-export keeps the same
 * call sites (`toast.success(...)`, `toast.error(...)`, `toast(...)`) but
 * applies the duration default unless the caller passes one explicitly.
 *
 * Timer alarms route through `toastAlarm` (12s) so the intent is documented
 * and they don't get caught by future audits.
 */
import { toast as sonner } from "sonner";

const SUCCESS_MS = 2500;
const INFO_MS = 4000;
const ERROR_MS = 6000;
const ALARM_MS = 12000;

type ToastData = Parameters<typeof sonner>[1];

const withDuration = (defaultMs: number, data?: ToastData): ToastData => ({
  duration: defaultMs,
  ...(data ?? {}),
});

type ToastFn = ((message: Parameters<typeof sonner>[0], data?: ToastData) => ReturnType<typeof sonner>) & {
  success: typeof sonner.success;
  error: typeof sonner.error;
  info: typeof sonner.info;
  warning: typeof sonner.warning;
  message: typeof sonner.message;
  loading: typeof sonner.loading;
  promise: typeof sonner.promise;
  dismiss: typeof sonner.dismiss;
  custom: typeof sonner.custom;
};

const wrapped = ((message, data) =>
  sonner(message, withDuration(INFO_MS, data))) as ToastFn;

wrapped.success = ((message, data) =>
  sonner.success(message, withDuration(SUCCESS_MS, data))) as typeof sonner.success;
wrapped.error = ((message, data) =>
  sonner.error(message, withDuration(ERROR_MS, data))) as typeof sonner.error;
wrapped.info = ((message, data) =>
  sonner.info(message, withDuration(INFO_MS, data))) as typeof sonner.info;
wrapped.warning = ((message, data) =>
  sonner.warning(message, withDuration(ERROR_MS, data))) as typeof sonner.warning;
wrapped.message = ((message, data) =>
  sonner.message(message, withDuration(INFO_MS, data))) as typeof sonner.message;
wrapped.loading = sonner.loading;
wrapped.promise = sonner.promise;
wrapped.dismiss = sonner.dismiss;
wrapped.custom = sonner.custom;

export const toast = wrapped;

/** Long-lived "alarm" toast for finished cooking timers. */
export const toastAlarm: typeof sonner = ((message, data) =>
  sonner(message, withDuration(ALARM_MS, data))) as typeof sonner;
